import { useMemo, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";

import { fota } from "@/api/client";
import { MultiSelect } from "@/components/MultiSelect";

/** One company's reach: its benches, and whether it also gets the build by air. */
export interface ReachRow {
  company_id: string;
  cps_ids: string[];
  fota: boolean;
}

/** A build's reach as a TICK MATRIX: companies down, benches across, FOTA last.
 *
 *  Dattatray: *"Why cant have a tick matrix : for view and edit?"* and, of the
 *  card version this replaces, *"too mnay words"*.
 *
 *  I ARGUED A GRID COULD NOT BE DRAWN, AND I WAS WRONG. The objection was that
 *  benches belong to one company each, so a company x bench grid would be
 *  mostly holes. It is not: **spaces are fixed per kind** (D-89 -- the
 *  vocabulary is identical for every company holding that product and nobody
 *  adds a private one), so the columns are a FIXED SET and the grid is dense.
 *  A cell is unavailable only where that company has not been ALLOCATED that
 *  space, which greys one cell rather than leaving a structural hole.
 *
 *  ONE SHAPE FOR VIEW AND EDIT. `disabled` renders the same grid read-only
 *  rather than a different summary, so what an operator reads sits exactly
 *  where they would have ticked it.
 *
 *  NO PROSE. The cards explained every row in a sentence ("Both: written at
 *  Board Production, Engineering Testing, Engineering, and rollouts to this
 *  company are allowed") because three ticks across two opposite empty-state
 *  conventions are genuinely confusing. A grid does not need the sentence: the
 *  ticks sit in named columns and the air column is visibly separated. The one
 *  fact a grid cannot show is what NO companies means -- empty here means
 *  visible to ALL -- so that survives, as the picker's own empty label.
 *
 *  COLUMN ORDER IS THE BOARD'S TRAVEL ORDER, served by `publishable_spaces`
 *  from `seed.COMMON_SPACES` rather than copied here: Engineering, Engineering
 *  Testing, Customer Testing, Board Production, the kind's production space,
 *  Deployment, Repair.
 */
export function ReachMatrix({ value, onChange, companies, kind, disabled = false }: {
  value: ReachRow[];
  onChange: (rows: ReachRow[]) => void;
  companies: { company_id: string; name: string }[];
  /** The build's product. Benches cannot be offered without it, because a
   *  `cps_id` is (company, product, space) and the product is half the key. */
  kind: string | null;
  disabled?: boolean;
}) {
  const named = value.map((r) => r.company_id);

  // ONE query for every named company, not one per row (D-107).
  const spaces = useQuery({
    queryKey: ["fota", "spaces", kind, [...named].sort().join(",")],
    queryFn: () => fota.firmwareSpaces(kind as string, named),
    enabled: !!kind && named.length > 0,
  });

  /** The columns, plus the (company, slug) -> cps_id lookup each cell needs. */
  const { columns, cell } = useMemo(() => {
    const cols = new Map<string, { slug: string; name: string; order: number }>();
    const byCell = new Map<string, string>();
    for (const sp of spaces.data?.spaces ?? []) {
      // `order` comes from the server. Falling back to a large number means a
      // space from an older server sorts LAST rather than leading the row.
      if (!cols.has(sp.slug)) {
        cols.set(sp.slug, { slug: sp.slug, name: sp.name, order: sp.order ?? 9999 });
      }
      byCell.set(`${sp.company_id}\u0000${sp.slug}`, sp.cps_id);
    }
    return {
      columns: [...cols.values()]
        .sort((a, b) => a.order - b.order || a.slug.localeCompare(b.slug)),
      cell: byCell,
    };
  }, [spaces.data]);

  const companyName = (id: string) =>
    companies.find((c) => c.company_id === id)?.name ?? id;

  const setCompanies = (ids: string[]) => {
    // Rows that stay keep their ticks, so toggling one company never disturbs
    // another's. A re-selected company starts fresh.
    const byId = new Map(value.map((r) => [r.company_id, r]));
    onChange(ids.map((id) => byId.get(id) ?? { company_id: id, cps_ids: [], fota: false }));
  };

  const toggleBench = (cid: string, cps: string, on: boolean) =>
    onChange(value.map((r) => r.company_id !== cid ? r : {
      ...r,
      cps_ids: on ? [...r.cps_ids, cps] : r.cps_ids.filter((c) => c !== cps),
    }));

  const toggleAir = (cid: string, on: boolean) =>
    onChange(value.map((r) => (r.company_id === cid ? { ...r, fota: on } : r)));

  // Sized for a dialog that now has 1400px, not 600px. The header cells get
  // real horizontal padding so "Board Production" and "Engineering Testing"
  // read as separate columns instead of a run of words, and the rows get
  // enough height to hit a checkbox without aiming.
  const th: CSSProperties = {
    fontSize: 11.5, color: "var(--t-mute)", fontWeight: 400, padding: "0 14px 9px",
    textAlign: "center", verticalAlign: "bottom", lineHeight: 1.3, whiteSpace: "nowrap",
  };
  const td: CSSProperties = {
    textAlign: "center", padding: "9px 14px", borderTop: "1px solid var(--line)",
  };
  const airEdge = "2px solid var(--line2)";

  return (
    <div>
      <MultiSelect
        disabled={disabled}
        options={companies.map((c) => ({ value: c.company_id, label: c.name }))}
        value={named}
        onChange={(v) => setCompanies(v as string[])}
        emptyLabel="No company - visible to ALL, no bench, no air"
        clearLabel="Clear - visible to all" />

      {value.length > 0 && (
        <div style={{ overflowX: "auto", marginTop: 10 }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left", paddingLeft: 2, minWidth: 190 }}>Company</th>
                {columns.map((c) => (
                  <th key={c.slug} style={th} title={`${c.name} (${c.slug})`}>{c.name}</th>
                ))}
                {/* THE AIR IS NOT A BENCH, and the rule separator says so
                    without a sentence: FOTA ignores spaces entirely (D-126),
                    so it must not read as one more column of the same kind. */}
                <th style={{ ...th, borderLeft: airEdge, minWidth: 58 }}
                    title="Available for FOTA - rollouts to this company are allowed">FOTA</th>
              </tr>
            </thead>
            <tbody>
              {value.map((row) => (
                <tr key={row.company_id}>
                  <td style={{ ...td, textAlign: "left", paddingLeft: 2, fontSize: 12.5,
                               color: "var(--t-bright)" }}>
                    {companyName(row.company_id)}
                  </td>
                  {columns.map((c) => {
                    const cps = cell.get(`${row.company_id}\u0000${c.slug}`);
                    // NOT ALLOCATED -> NOT TICKABLE. `set_reach` refuses a bench
                    // that is not this company's, so the grid must not offer
                    // what the save would reject.
                    return (
                      <td key={c.slug} style={td}>
                        <input type="checkbox"
                          checked={!!cps && row.cps_ids.includes(cps)}
                          disabled={disabled || !cps}
                          title={cps ? undefined
                                 : `${companyName(row.company_id)} has no ${c.name} bench for this product`}
                          onChange={(e) => cps && toggleBench(row.company_id, cps, e.target.checked)} />
                      </td>
                    );
                  })}
                  <td style={{ ...td, borderLeft: airEdge }}>
                    <input type="checkbox" checked={row.fota} disabled={disabled}
                      onChange={(e) => toggleAir(row.company_id, e.target.checked)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!kind && (
            <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
              This build declares no model, so it has no product and no benches.
            </div>
          )}
          {kind && spaces.isFetching && (
            <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>loading benches…</div>
          )}
          {kind && !spaces.isFetching && columns.length === 0 && (
            <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
              No benches allocated to these companies for this product.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
